import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { isExecutionLockResetAckRequired } from '@/features/work-management/taskClosure';
import type { TaskClosureKind } from '@/features/work-management/workTasks.api';

const EXECUTION_LOCK_RESET_ACK_COPY =
  "This phase can't start until the previous gate is approved — moving this task returns it to To Do.";

export const CLOSURE_KIND_LABEL: Record<TaskClosureKind, string> = {
  CANCELLED: 'Cancel this task',
  NOT_APPLICABLE: 'Mark not applicable',
  DEFERRED: 'Defer to the next phase',
};

export function isBlankClosureReason(value: string): boolean {
  return value.trim() === '';
}

export function TaskClosureReasonDialog({
  kind,
  taskTitle,
  busy,
  onCancel,
  onConfirm,
}: {
  kind: TaskClosureKind;
  taskTitle: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (
    reason: string,
    options?: { acknowledgeStatusReset?: boolean },
  ) => void | Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [acknowledgeReset, setAcknowledgeReset] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const blank = isBlankClosureReason(reason);

  async function submit(acknowledge: boolean) {
    setSubmitting(true);
    try {
      await onConfirm(
        reason.trim(),
        acknowledge ? { acknowledgeStatusReset: true } : undefined,
      );
    } catch (err) {
      if (!acknowledge && isExecutionLockResetAckRequired(err)) {
        setAcknowledgeReset(true);
      }
    } finally {
      setSubmitting(false);
    }
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close reason dialog"
        onClick={onCancel}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="task-closure-title"
        data-testid="task-closure-dialog"
        className="relative z-10 w-full max-w-md rounded-lg bg-white p-4 shadow-xl"
      >
        <h2 id="task-closure-title" className="text-base font-semibold text-gray-900">
          {CLOSURE_KIND_LABEL[kind]}
        </h2>
        {acknowledgeReset ? (
          <p className="mt-3 text-sm text-gray-700">{EXECUTION_LOCK_RESET_ACK_COPY}</p>
        ) : (
          <>
            <p className="mt-1 text-sm text-gray-600">{taskTitle}</p>
            <label className="mt-3 block text-sm font-medium" htmlFor="task-closure-reason">
              Reason (required)
            </label>
            <textarea
              id="task-closure-reason"
              data-testid="task-closure-reason"
              className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </>
        )}
        <div className="mt-3 flex items-center justify-end gap-2">
          {!acknowledgeReset && blank ? (
            <p className="mr-auto text-sm text-gray-600" data-testid="task-closure-reason-required">
              A reason is required
            </p>
          ) : null}
          <button type="button" className="rounded border px-3 py-1 text-sm" onClick={onCancel}>
            {acknowledgeReset ? 'Cancel' : 'Back'}
          </button>
          {acknowledgeReset ? (
            <button
              type="button"
              data-testid="task-closure-ack-confirm"
              className="rounded bg-gray-900 px-3 py-1 text-sm text-white disabled:opacity-50"
              disabled={busy || submitting}
              onClick={() => void submit(true)}
            >
              Confirm
            </button>
          ) : (
            <button
              type="button"
              data-testid="task-closure-confirm"
              className="rounded bg-gray-900 px-3 py-1 text-sm text-white disabled:opacity-50"
              disabled={blank || busy || submitting}
              onClick={() => void submit(false)}
            >
              Save reason
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
